import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Building2, Lock, Mail, Loader2, ArrowLeft } from 'lucide-react'
import { useAuthStore } from '@/stores/auth-store'
import { authApi } from '@/services/api'
import { deriveRole } from '@/components/ProtectedRoute'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  const login = useAuthStore((state) => state.login)

  const loginMutation = useMutation({
    mutationFn: async () => {
      // First, get the tokens
      const loginResponse = await authApi.login({ email, password })
      const token = loginResponse.data.access_token
      const refreshToken = loginResponse.data.refresh_token ?? null

      // Store tokens temporarily in localStorage for the next request
      localStorage.setItem('auth_token', token)
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken)
      }

      // Then fetch user data
      const userResponse = await authApi.me()

      return {
        user: userResponse.data,
        token,
        refreshToken,
      }
    },
    onSuccess: (data) => {
      login(data.user, data.token, data.refreshToken)
      const role = deriveRole(data.user)
      if (role === 'resident') {
        toast.success('Zalogowano do Panelu Mieszkańca', {
          description: `Witaj, ${data.user.first_name || data.user.email}!`,
        })
        navigate('/portal/dashboard', { replace: true })
        return
      }
      toast.success('Zalogowano pomyślnie', {
        description: `Witaj, ${data.user.first_name || data.user.email}!`,
      })
      navigate('/dashboard')
    },
    onError: (error: any) => {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      toast.error('Błąd logowania', {
        description: error.response?.data?.detail || 'Sprawdź swoje dane i spróbuj ponownie',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    loginMutation.mutate()
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 relative overflow-hidden">
      {/* Back to landing link */}
      <Link
        to="/"
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-gray-600 hover:text-primary transition-colors font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Wróć do strony głównej
      </Link>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-blue-300/10 to-purple-300/10 rounded-full blur-3xl" />
      </div>

      {/* Login card */}
      <Card className="w-full max-w-md relative z-10 shadow-2xl border-white/20">
        <CardHeader className="space-y-4 text-center pb-8">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/25 transform hover:scale-110 transition-transform duration-300">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-3xl font-bold text-primary">
              ZAMEK
            </CardTitle>
            <CardDescription className="text-base text-gray-600">
              System zarządzania rozliczeniami wspólnot mieszkaniowych
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Adres email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="twoj@email.pl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11"
                    disabled={loginMutation.isPending}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  Hasło
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11"
                    disabled={loginMutation.isPending}
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Logowanie...
                </>
              ) : (
                'Zaloguj się'
              )}
            </Button>

            <div className="text-center text-sm text-gray-500 space-y-2">
              <p>Problemy z logowaniem?</p>
              <button
                type="button"
                className="text-primary hover:text-primary/80 font-medium transition-colors mt-1"
                onClick={() => toast.info('Skontaktuj się z administratorem')}
              >
                Skontaktuj się z administratorem
              </button>
              <p className="pt-2 border-t border-gray-100">
                Jesteś mieszkańcem?{' '}
                <Link
                  to="/portal/login"
                  className="text-primary hover:underline font-medium"
                >
                  Przejdź do Panelu Mieszkańca
                </Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-gray-500 z-10">
        <p>&copy; 2025 ZAMEK. Wszystkie prawa zastrzeżone.</p>
      </div>
    </div>
  )
}
